/**
 * MaintenanceBanner — sticky banner shown 24h before maintenance starts until it ends.
 *
 * Features:
 *   - Auto-displays when current_time >= (start_at - 24h) AND current_time < end_at
 *   - Hides when current_time >= end_at
 *   - Yellow background when scheduled; red when active (start_at <= now)
 *   - Countdown timer: "HH:mm remaining" when < 1 hour until start_at
 *   - Polls GET /api/status/maintenance?status=scheduled,active every 60 seconds
 *   - Refreshes on page visibility change (visibilitychange event)
 *   - Responsive: sticky full-width on mobile (<768px), centered on desktop
 *   - Accessible: aria-live="polite", aria-label describing maintenance status
 *   - Retries failed API calls 3× with exponential backoff (1s, 3s, 9s)
 *
 * Props:
 *   maintenanceWindow — override; if null the component fetches its own data
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { statusApi, type MaintenanceWindow } from '../../api/status.js';

// ── Constants ─────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 60_000;
const BANNER_LEAD_MS = 24 * 60 * 60 * 1000; // 24h before start_at
// Circuit-break: stop polling after this many consecutive failures so a dead
// status-service doesn't spam 502s every minute.
const MAX_CONSECUTIVE_FAILURES = 3;

// ── Helpers ───────────────────────────────────────────────────────────────────

function isWindowVisible(win: MaintenanceWindow, now: Date): boolean {
  const startAt = new Date(win.start_at ?? win.scheduled_start ?? '');
  const endAt = new Date(win.end_at ?? win.scheduled_end ?? '');
  const showFrom = new Date(startAt.getTime() - BANNER_LEAD_MS);
  return now >= showFrom && now < endAt;
}

function isWindowActive(win: MaintenanceWindow, now: Date): boolean {
  const startAt = new Date(win.start_at ?? win.scheduled_start ?? '');
  const endAt = new Date(win.end_at ?? win.scheduled_end ?? '');
  return now >= startAt && now < endAt;
}

function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Returns "HH:mm" countdown string if within 1 hour, else null. */
function getCountdown(startAtIso: string, now: Date): string | null {
  const startAt = new Date(startAtIso);
  const msUntilStart = startAt.getTime() - now.getTime();
  if (msUntilStart <= 0 || msUntilStart > 60 * 60 * 1000) return null;
  const totalMinutes = Math.ceil(msUntilStart / 60_000);
  const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
  const minutes = (totalMinutes % 60).toString().padStart(2, '0');
  return `${hours}:${minutes} remaining`;
}

/** Retry fetch with exponential backoff: 1s, 3s, 9s. */
async function fetchWithRetry<T>(fn: () => Promise<T>, maxAttempts = 3): Promise<T> {
  const delays = [1000, 3000, 9000];
  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
      }
    }
  }
  throw lastErr;
}

// ── Hook: poll for active/scheduled maintenance ───────────────────────────────

function useMaintenance(externalWindow: MaintenanceWindow | null | undefined) {
  const [window, setWindow] = useState<MaintenanceWindow | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const failuresRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchMaintenance = useCallback(async () => {
    if (!mountedRef.current) return;
    let succeeded = false;

    try {
      const result = await fetchWithRetry(() =>
        statusApi.getMaintenanceWindows({ status: 'scheduled,active', limit: 100 }),
      );
      if (!mountedRef.current) return;

      const now = new Date();
      // Find the most relevant window (active first, then soonest scheduled)
      const visible = result.maintenance_windows.filter((w) => isWindowVisible(w, now));
      const active = visible.find((w) => isWindowActive(w, now));
      const scheduled = visible
        .filter((w) => !isWindowActive(w, now))
        .sort((a, b) => new Date(a.start_at ?? a.scheduled_start ?? '').getTime() -
                         new Date(b.start_at ?? b.scheduled_start ?? '').getTime())[0];

      setWindow(active ?? scheduled ?? null);
      succeeded = true;
      failuresRef.current = 0;
    } catch {
      failuresRef.current += 1;
    } finally {
      if (
        mountedRef.current &&
        failuresRef.current < MAX_CONSECUTIVE_FAILURES
      ) {
        const factor = succeeded ? 1 : Math.pow(4, failuresRef.current);
        const delay = Math.min(POLL_INTERVAL_MS * factor, 10 * 60_000);
        timerRef.current = setTimeout(fetchMaintenance, delay);
      }
    }
  }, []);

  useEffect(() => {
    // If external window is provided, use it directly
    if (externalWindow !== undefined) {
      setWindow(externalWindow);
      return;
    }

    fetchMaintenance();

    // Refresh on page visibility change
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        if (timerRef.current) clearTimeout(timerRef.current);
        fetchMaintenance();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchMaintenance, externalWindow]);

  return window;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface MaintenanceBannerProps {
  /**
   * If provided, use this window directly instead of polling.
   * Pass `null` to explicitly show nothing.
   * Omit (undefined) to let the component fetch its own data.
   */
  maintenanceWindow?: MaintenanceWindow | null;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function MaintenanceBanner({ maintenanceWindow }: MaintenanceBannerProps) {
  const win = useMaintenance(maintenanceWindow);
  const [now, setNow] = useState(() => new Date());

  // Update "now" every minute for countdown timer
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  if (!win) return null;

  const startAt = win.start_at ?? win.scheduled_start ?? '';
  const endAt = win.end_at ?? win.scheduled_end ?? '';

  // Don't display if end_at has passed
  if (!isWindowVisible(win, now)) return null;

  const active = isWindowActive(win, now) || win.status === 'in_progress' || win.status === 'active';
  const countdown = getCountdown(startAt, now);

  const ariaLabel = active
    ? `Active maintenance: ${win.title}`
    : `Scheduled maintenance: ${win.title}`;

  return (
    <div
      aria-live="polite"
      aria-label={ariaLabel}
      style={{
        backgroundColor: active ? 'var(--color-error-bg, #fef2f2)' : 'var(--color-warning-bg, #fefce8)',
      }}
      className={[
        // Mobile: full-width sticky top
        'w-full sticky top-0 z-50',
        // Desktop: centered banner
        'md:static md:w-full',
        'border-b px-4 py-2.5',
        active
          ? 'border-red-200 text-red-800'
          : 'border-yellow-200 text-yellow-800',
      ].join(' ')}
    >
      {/* Desktop: centered wrapper */}
      <div className="max-w-4xl mx-auto flex items-center gap-3 text-sm"
           style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>

        {/* Icon */}
        <span className="flex-shrink-0" aria-hidden="true">
          {active ? (
            // Maintenance (wrench) icon
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ) : (
            // Warning icon
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
        </span>

        {/* Content */}
        <Link
          to={`/status/maintenance/${win.id}`}
          className="flex-1 min-w-0 hover:underline focus:underline focus:outline-none"
          style={{ padding: 'var(--spacing-md, 0)' }}
        >
          <span className="font-medium">Scheduled maintenance:</span>{' '}
          <span className="truncate">
            {win.title} — {formatDateTime(startAt)} to {formatDateTime(endAt)}
          </span>
        </Link>

        {/* Countdown timer (< 1h until start) */}
        {countdown && !active && (
          <span className="flex-shrink-0 font-mono font-medium opacity-80" aria-live="polite">
            {countdown}
          </span>
        )}
      </div>
    </div>
  );
}

export default MaintenanceBanner;
